import { NavLink, useNavigate } from 'react-router-dom';

import { Loading } from '../common';
import { relativeTime } from '../../utils/format';
import './layout.css';

const NAV = [
  { to: '/batch', label: 'Batch scoring', glyph: '◲' },
  { to: '/compare', label: 'Compare versions', glyph: '⇄' },
  { to: '/search', label: 'Search', glyph: '◎' },
  { to: '/settings/prompts', label: 'Settings', glyph: '⚙' },
];

export function Sidebar({
  conversations,
  loading,
  activeConversationId,
  onNewAnalysis,
  onSelectConversation,
  onDeleteConversation,
  onPinConversation,
  runningJobs = 0,
}) {
  const navigate = useNavigate();

  // Pinned first, in their own group: a pinned chat that stayed under "Earlier"
  // would be sorted to the top of a section the eye skips.
  const pinned = [];
  const today = [];
  const earlier = [];
  const dayStart = new Date();
  dayStart.setHours(0, 0, 0, 0);
  conversations.forEach((conversation) => {
    if (conversation.is_pinned) {
      pinned.push(conversation);
      return;
    }
    const updated = new Date(`${conversation.updated_at}Z`);
    (updated >= dayStart ? today : earlier).push(conversation);
  });

  return (
    <nav className="rail" aria-label="Navigation">
      <div className="rail__brand">
        <span className="rail__logo" aria-hidden="true">
          iR
        </span>
        VLM Studio
      </div>

      <button type="button" className="rail__new" onClick={onNewAnalysis}>
        <span aria-hidden="true">＋</span> New analysis
      </button>

      <div className="rail__nav">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `rail__link ${isActive ? 'rail__link--active' : ''}`}
          >
            <span aria-hidden="true">{item.glyph}</span>
            {item.label}
            {item.to === '/batch' && runningJobs > 0 && (
              <span className="rail__link-count">{runningJobs}</span>
            )}
          </NavLink>
        ))}
      </div>

      <div className="rail__list">
        {loading ? (
          <Loading label="" />
        ) : conversations.length === 0 ? (
          <div className="rail__section xs subtle" style={{ paddingTop: 4 }}>
            No analyses yet. Drop a road image into the composer to start one.
          </div>
        ) : (
          <>
            {[
              { label: 'Pinned', items: pinned },
              { label: 'Today', items: today },
              { label: 'Earlier', items: earlier },
            ].map((group) => (
              <ConversationGroup
                key={group.label}
                label={group.label}
                items={group.items}
                activeId={activeConversationId}
                onSelect={onSelectConversation}
                onDelete={onDeleteConversation}
                onPin={onPinConversation}
              />
            ))}
          </>
        )}
      </div>

      <div className="rail__foot">
        <button
          type="button"
          className="rail__link"
          onClick={() => navigate('/settings/system')}
          title="Worker, GPU and configuration"
        >
          <span aria-hidden="true">◉</span> System status
        </button>
      </div>
    </nav>
  );
}

function ConversationGroup({ label, items, activeId, onSelect, onDelete, onPin }) {
  if (!items.length) return null;
  return (
    <>
      <div className="rail__section section-label" style={{ paddingTop: 10 }}>
        {label}
      </div>
      {items.map((conversation) => (
        // conv-row, and the hover rules hang off it. The actions are siblings of
        // .conv, not children — a button cannot be nested inside a button — so the
        // old `.conv:hover .conv__action` never matched anything and the delete
        // control was invisible at every screen size.
        <div key={conversation.id} className="conv-row">
          <button
            type="button"
            className={`conv ${conversation.id === activeId ? 'conv--active' : ''}`}
            onClick={() => onSelect(conversation.id)}
          >
            <div className="conv__title">
              {conversation.is_pinned && (
                <span className="conv__pinned" aria-label="Pinned" title="Pinned">
                  ◆
                </span>
              )}
              {conversation.title}
            </div>
            <div className="conv__meta">
              {conversation.version_id || 'no version'} · {conversation.message_count} msg ·{' '}
              {relativeTime(conversation.updated_at)}
            </div>
          </button>
          <div className="conv__actions">
            <button
              type="button"
              className={`conv__action ${conversation.is_pinned ? 'conv__action--on' : ''}`}
              aria-label={`${conversation.is_pinned ? 'Unpin' : 'Pin'} ${conversation.title}`}
              title={conversation.is_pinned ? 'Unpin' : 'Pin to the top'}
              onClick={(event) => {
                event.stopPropagation();
                onPin?.(conversation.id, !conversation.is_pinned);
              }}
            >
              ◆
            </button>
            <button
              type="button"
              className="conv__action conv__action--danger"
              aria-label={`Delete ${conversation.title}`}
              title="Delete this chat"
              onClick={(event) => {
                event.stopPropagation();
                onDelete(conversation.id);
              }}
            >
              ✕
            </button>
          </div>
        </div>
      ))}
    </>
  );
}
